import React, { useEffect, useRef, useState } from "react";
import { HERO_VIDEO_SOURCES } from "./data";

/**
 * Hero — full viewport video with the oval emblem / wordmark / eyebrow
 * stack. CTAs live in the HeroPillarStrip component below the hero.
 */
const Hero = () => {
  const videoRef = useRef(null);

  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [
      setTimeout(() => setStage(1), 250),
      setTimeout(() => setStage(2), 600),
      setTimeout(() => setStage(3), 950),
      setTimeout(() => setStage(4), 1300),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const heroTextShadow =
    "0 2px 18px rgba(28,20,16,0.55), 0 1px 3px rgba(28,20,16,0.45)";

  return (
    <section
      className="relative w-full h-screen min-h-[680px] overflow-hidden bg-num-deep"
      data-testid="hero-section"
    >
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ zIndex: 0 }}
        autoPlay muted loop playsInline preload="auto"
        aria-hidden="true"
        data-testid="hero-video"
      >
        {HERO_VIDEO_SOURCES.map((src) => (
          <source key={src} src={src} type="video/mp4" />
        ))}
      </video>

      {/* Dark overlay — light tint so video stays visible */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ zIndex: 1, backgroundColor: "rgba(10,8,5,0.35)" }}
        aria-hidden="true"
      />
      {/* Bottom vignette for scroll cue legibility */}
      <div
        className="absolute inset-x-0 bottom-0 pointer-events-none"
        style={{
          zIndex: 1,
          height: "30%",
          background: "linear-gradient(to top, rgba(0,0,0,0.3) 0%, transparent 100%)",
        }}
        aria-hidden="true"
      />

      <div
        className="relative h-full flex flex-col items-center justify-center text-center px-6"
        style={{ zIndex: 2 }}
      >
        {/* Element 1 — Equestrian emblem */}
        <img
          src="/HNMB_logo_.svg"
          alt=""
          draggable={false}
          className={`block transition-all duration-700 ${
            stage >= 1 ? "opacity-100 scale-100" : "opacity-0 scale-90"
          }`}
          style={{
            width: 110,
            height: "auto",
            marginBottom: 28,
            filter:
              "brightness(0) saturate(100%) invert(97%) sepia(8%) saturate(300%) hue-rotate(330deg) brightness(102%)",
          }}
          data-testid="hero-emblem"
        />

        {/* Element 2 — "HOUSE OF" */}
        <p
          className={`transition-all duration-700 ${
            stage >= 2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
          }`}
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontWeight: 300,
            fontSize: 18,
            letterSpacing: "0.55em",
            color: "rgba(245,237,216,0.85)",
            textTransform: "uppercase",
            marginBottom: 4,
          }}
        >
          House of
        </p>

        {/* Element 3 — "NUMBARDAR" */}
        <p
          className={`transition-all duration-700 ${
            stage >= 2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
          }`}
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontStyle: "italic",
            fontWeight: 400,
            fontSize: "clamp(52px, 10vw, 120px)",
            color: "#C8A95A",
            letterSpacing: "0.05em",
            lineHeight: 1,
            marginBottom: 24,
          }}
          data-testid="hero-title-line1"
        >
          Numbardar
        </p>

        {/* Element 4 — Thin gold rule */}
        <div
          className={`transition-all duration-700 ${
            stage >= 3 ? "opacity-100" : "opacity-0"
          }`}
          style={{
            width: 120,
            height: 1,
            backgroundColor: "rgba(200,169,90,0.5)",
            margin: "0 auto 20px auto",
          }}
          aria-hidden="true"
        />

        {/* Element 5 — Legacy eyebrow */}
        <p
          className={`transition-all duration-700 ${
            stage >= 3 ? "opacity-100" : "opacity-0"
          }`}
          style={{
            fontFamily: "'Jost', sans-serif",
            fontWeight: 200,
            fontSize: 10,
            letterSpacing: "0.5em",
            color: "rgba(245,237,216,0.6)",
            textTransform: "uppercase",
          }}
          data-testid="hero-legacy-eyebrow"
        >
          Legacy Since 1 7 9 5
        </p>

        {/* SCROLL cue */}
        <div
          className={`absolute bottom-7 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 transition-opacity duration-700 ${
            stage >= 4 ? "opacity-100" : "opacity-0"
          }`}
        >
          <span
            className="font-ui text-[9px] uppercase text-num-gold"
            style={{ letterSpacing: "0.5em", textShadow: heroTextShadow }}
          >
            Scroll
          </span>
          <div className="relative w-px h-12 bg-[rgba(200,169,90,0.25)] overflow-hidden">
            <span
              className="absolute top-0 left-0 w-px h-full bg-num-gold origin-top animate-scroll-pulse"
              style={{ transformOrigin: "top" }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
